import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { ClockIcon, PencilIcon, PlusIcon, XMarkIcon, CalendarIcon } from '@heroicons/react/24/outline';
import { useUnsavedChanges } from '../../hooks/useUnsavedChanges';

const OpeningHours = () => {
  const [operatingHours, setOperatingHours] = useState([]);
  const [specialHours, setSpecialHours] = useState([]);
  const [editingOperatingHours, setEditingOperatingHours] = useState(false);
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);
  const [originalOperatingHours, setOriginalOperatingHours] = useState([]);
  const [loading, setLoading] = useState(true);

  // Use the unsaved changes hook
  useUnsavedChanges(hasUnsavedChanges, editingOperatingHours);

  // Helper function to format time display (defined first)
  const formatTimeDisplay = (timeString) => {
    const [hour, minute] = timeString.split(':');
    const hourNum = parseInt(hour);
    const period = hourNum >= 12 ? 'PM' : 'AM';
    const displayHour = hourNum === 0 ? 12 : hourNum > 12 ? hourNum - 12 : hourNum;
    return `${displayHour}:${minute} ${period}`;
  };

  // Generate time options (15-minute intervals)
  const generateTimeOptions = () => {
    const times = [];
    for (let hour = 0; hour < 24; hour++) {
      for (let minute = 0; minute < 60; minute += 15) {
        const timeString = `${hour.toString().padStart(2, '0')}:${minute.toString().padStart(2, '0')}`;
        const displayTime = formatTimeDisplay(timeString);
        times.push({ value: timeString, display: displayTime });
      }
    }
    return times;
  };

  const timeOptions = generateTimeOptions();

  const dayTypeOptions = [
    'Every day',
    'Weekdays',
    'Weekends',
    'Monday',
    'Tuesday',
    'Wednesday',
    'Thursday',
    'Friday',
    'Saturday',
    'Sunday'
  ];

  const formatDayDisplay = (dayType) => {
    const dayMap = {
      'Every day': '7 days a week',
      'Weekdays': 'Monday - Friday',
      'Weekends': 'Saturday - Sunday',
      'Monday': 'Mondays',
      'Tuesday': 'Tuesdays',
      'Wednesday': 'Wednesdays',
      'Thursday': 'Thursdays',
      'Friday': 'Fridays',
      'Saturday': 'Saturdays',
      'Sunday': 'Sundays'
    };
    return dayMap[dayType] || dayType;
  };

  // Load opening hours from API
  useEffect(() => {
    const fetchOpeningHours = async () => {
      try {
        const response = await axios.get('/api/settings/organization/hours');
        const { data } = response.data;
        
        // Parse operating hours from response or use default
        let loadedOperatingHours = [];
        if (data && data.hours) {
          loadedOperatingHours = data.hours;
        }
        
        // If no operating hours exist, set default
        if (loadedOperatingHours.length === 0) {
          loadedOperatingHours = [
            {
              id: 1,
              dayType: 'Every day',
              openTime: '09:00',
              closeTime: '17:00',
              isClosed: false
            }
          ];
        }
        
        // Parse special hours from stored JSON or use empty array
        let loadedSpecialHours = [];
        if (data && data.special_hours) {
          try {
            loadedSpecialHours = JSON.parse(data.special_hours);
          } catch (e) {
            console.error('Error parsing special hours:', e);
          }
        }
        
        setOperatingHours(loadedOperatingHours);
        setOriginalOperatingHours([...loadedOperatingHours]);
        setSpecialHours(loadedSpecialHours);
        setLoading(false);
      } catch (error) {
        console.error('Error fetching opening hours:', error);
        // Initialize with default data if fetch fails
        const defaultOperatingHours = [
          {
            id: 1,
            dayType: 'Every day',
            openTime: '09:00',
            closeTime: '17:00',
            isClosed: false
          }
        ];
        setOperatingHours(defaultOperatingHours);
        setOriginalOperatingHours([...defaultOperatingHours]);
        setSpecialHours([]);
        setLoading(false);
      }
    };
    
    fetchOpeningHours();
  }, []);

  // Check for unsaved changes
  useEffect(() => {
    const hasChanges = JSON.stringify(operatingHours) !== JSON.stringify(originalOperatingHours);
    setHasUnsavedChanges(hasChanges);
  }, [operatingHours, originalOperatingHours]);

  // Unsaved changes protection is handled by the useUnsavedChanges hook

  const handleEditOperatingHours = () => {
    setOriginalOperatingHours([...operatingHours]);
    setEditingOperatingHours(true);
  };

  const handleCancelEdit = () => {
    if (hasUnsavedChanges) {
      if (window.confirm('Your changes have not been saved - are you sure you want to cancel?')) {
        setOperatingHours([...originalOperatingHours]);
        setEditingOperatingHours(false);
        setHasUnsavedChanges(false);
      }
    } else {
      setEditingOperatingHours(false);
    }
  };

  const handleSaveOperatingHours = async () => {
    try {
      // Send data in the format expected by the new modular endpoint
      const hoursData = {
        hours: operatingHours,
        specialHours: specialHours
      };
      
      console.log('Saving operating hours:', hoursData);
      
      await axios.put('/api/settings/organization/hours', hoursData);
      
      setOriginalOperatingHours([...operatingHours]);
      setEditingOperatingHours(false);
      setHasUnsavedChanges(false);
      alert('Operating hours saved successfully!');
    } catch (error) {
      console.error('Error saving operating hours:', error);
      alert('Failed to save operating hours. Please try again.');
    }
  };

  const handleOperatingHourChange = (id, field, value) => {
    setOperatingHours(prev => prev.map(hour => 
      hour.id === id ? { ...hour, [field]: value } : hour
    ));
  };

  const addOperatingHourRow = () => {
    const newRow = {
      id: Date.now(),
      dayType: 'Every day',
      openTime: '09:00',
      closeTime: '17:00',
      isClosed: false
    };
    setOperatingHours(prev => [...prev, newRow]);
  };

  const removeOperatingHourRow = (id) => {
    if (operatingHours.length > 1) {
      setOperatingHours(prev => prev.filter(hour => hour.id !== id));
    }
  };

  const addSpecialHour = () => {
    const newSpecialHour = {
      id: Date.now(),
      date: '',
      openTime: '09:00',
      closeTime: '17:00',
      isClosed: false
    };
    setSpecialHours(prev => [...prev, newSpecialHour]);
  };

  const handleSpecialHourChange = (id, field, value) => {
    setSpecialHours(prev => prev.map(hour => 
      hour.id === id ? { ...hour, [field]: value } : hour
    ));
  };

  const removeSpecialHourRow = (id) => {
    setSpecialHours(prev => prev.filter(hour => hour.id !== id));
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-gray-900 mx-auto"></div>
          <p className="mt-4 text-gray-600">Loading opening hours...</p>
        </div>
      </div>
    );
  }

  return (
    <div>
      <div className="md:flex md:items-center md:justify-between">
        <div className="flex-1 min-w-0">
          <h2 className="text-2xl font-bold leading-7 text-gray-900 sm:text-3xl sm:truncate">
            Opening Hours
          </h2>
          <p className="mt-1 text-gray-600">
            Set your store hours and availability for customers
          </p>
        </div>
      </div>

      <div className="mt-8 space-y-6">
        {/* Operating Hours Card */}
        <div className="bg-white shadow rounded-lg p-6">
          <div className="flex items-center justify-between mb-6">
            <div className="flex items-center">
              <ClockIcon className="h-6 w-6 text-gray-400 mr-2" />
              <h3 className="text-lg font-medium text-gray-900">Operating Hours</h3>
            </div>
            {!editingOperatingHours && (
              <button
                onClick={handleEditOperatingHours}
                className="bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-lg flex items-center text-sm font-medium transition-colors duration-200"
              >
                <PencilIcon className="h-4 w-4 mr-2" />
                Edit
              </button>
            )}
          </div>

          {!editingOperatingHours ? (
            // View Mode
            <div className="space-y-3">
              {operatingHours.map((hour) => (
                <div key={hour.id} className="flex items-center justify-between py-3 border-b border-gray-100 last:border-b-0">
                  <div className="flex-1">
                    <span className="font-medium text-gray-900">
                      {formatDayDisplay(hour.dayType)}
                    </span>
                  </div>
                  <div className="text-gray-600">
                    {hour.isClosed ? (
                      <span className="text-red-600 font-medium">Closed</span>
                    ) : (
                      <span>
                        {formatTimeDisplay(hour.openTime)} - {formatTimeDisplay(hour.closeTime)}
                      </span>
                    )}
                  </div>
                </div>
              ))}
            </div>
          ) : (
            // Edit Mode
            <div className="space-y-4">
              {operatingHours.map((hour) => (
                <div key={hour.id} className="grid grid-cols-1 md:grid-cols-6 gap-4 items-end p-4 border border-gray-200 rounded-lg">
                  {/* Day Type Dropdown */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Day(s)
                    </label>
                    <select
                      value={hour.dayType}
                      onChange={(e) => handleOperatingHourChange(hour.id, 'dayType', e.target.value)}
                      className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    >
                      {dayTypeOptions.map(option => (
                        <option key={option} value={option}>{option}</option>
                      ))}
                    </select>
                  </div>

                  {/* Opening Time */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Opens
                    </label>
                    <select
                      value={hour.openTime}
                      onChange={(e) => handleOperatingHourChange(hour.id, 'openTime', e.target.value)}
                      disabled={hour.isClosed}
                      className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent disabled:bg-gray-100 disabled:text-gray-400"
                      style={{ maxHeight: '200px', overflowY: 'auto' }}
                    >
                      {timeOptions.map(time => (
                        <option key={time.value} value={time.value}>{time.display}</option>
                      ))}
                    </select>
                  </div>

                  {/* Closing Time */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Closes
                    </label>
                    <select
                      value={hour.closeTime}
                      onChange={(e) => handleOperatingHourChange(hour.id, 'closeTime', e.target.value)}
                      disabled={hour.isClosed}
                      className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent disabled:bg-gray-100 disabled:text-gray-400"
                      style={{ maxHeight: '200px', overflowY: 'auto' }}
                    >
                      {timeOptions.map(time => (
                        <option key={time.value} value={time.value}>{time.display}</option>
                      ))}
                    </select>
                  </div>

                  {/* Closed Checkbox */}
                  <div className="flex items-center">
                    <input
                      type="checkbox"
                      id={`closed-${hour.id}`}
                      checked={hour.isClosed}
                      onChange={(e) => handleOperatingHourChange(hour.id, 'isClosed', e.target.checked)}
                      className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                    />
                    <label htmlFor={`closed-${hour.id}`} className="ml-2 text-sm text-gray-700">
                      Closed
                    </label>
                  </div>

                  {/* Delete Button */}
                  <div className="flex justify-end">
                    <button
                      onClick={() => removeOperatingHourRow(hour.id)}
                      disabled={operatingHours.length === 1}
                      className="p-2 text-red-600 hover:text-red-800 hover:bg-red-50 rounded-lg disabled:text-gray-400 disabled:hover:bg-transparent disabled:cursor-not-allowed transition-colors duration-200"
                      title="Delete row"
                    >
                      <XMarkIcon className="h-5 w-5" />
                    </button>
                  </div>
                </div>
              ))}

              {/* Add Row Button */}
              <button
                onClick={addOperatingHourRow}
                className="w-full border-2 border-dashed border-gray-300 rounded-lg p-4 text-gray-600 hover:border-blue-500 hover:text-blue-600 transition-colors duration-200 flex items-center justify-center"
              >
                <PlusIcon className="h-5 w-5 mr-2" />
                Add Row
              </button>

              {/* Save/Cancel Buttons */}
              <div className="flex justify-end space-x-4 pt-6 border-t border-gray-200">
                <button
                  onClick={handleCancelEdit}
                  className="px-6 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-colors duration-200"
                >
                  Cancel
                </button>
                <button
                  onClick={handleSaveOperatingHours}
                  disabled={!hasUnsavedChanges}
                  className={`px-6 py-2 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-colors duration-200 ${
                    hasUnsavedChanges
                      ? 'bg-blue-600 hover:bg-blue-700'
                      : 'bg-gray-400 cursor-not-allowed'
                  }`}
                >
                  Save Changes
                </button>
              </div>
            </div>
          )}
        </div>

        {/* Special Hours Card */}
        <div className="bg-white shadow rounded-lg p-6">
          <div className="flex items-center justify-between mb-6">
            <div className="flex items-center">
              <CalendarIcon className="h-6 w-6 text-gray-400 mr-2" />
              <div>
                <h3 className="text-lg font-medium text-gray-900">Special Hours</h3>
                <p className="text-sm text-gray-600">Add special hours for holidays and other exceptions</p>
              </div>
            </div>
            <button
              onClick={addSpecialHour}
              className="bg-green-600 hover:bg-green-700 text-white px-4 py-2 rounded-lg flex items-center text-sm font-medium transition-colors duration-200"
            >
              <PlusIcon className="h-4 w-4 mr-2" />
              Add Exception
            </button>
          </div>

          {specialHours.length === 0 ? (
            <div className="text-center py-8 text-gray-500">
              <CalendarIcon className="h-12 w-12 mx-auto mb-4 text-gray-300" />
              <p>No special hours set. Click "Add Exception" to create special hours for holidays or specific dates.</p>
            </div>
          ) : (
            <div className="space-y-4">
              {specialHours.map((specialHour) => (
                <div key={specialHour.id} className="grid grid-cols-1 md:grid-cols-5 gap-4 items-end p-4 border border-gray-200 rounded-lg">
                  {/* Date Picker */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Date
                    </label>
                    <input
                      type="date"
                      value={specialHour.date}
                      onChange={(e) => handleSpecialHourChange(specialHour.id, 'date', e.target.value)}
                      className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    />
                  </div>

                  {/* Opening Time */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Opens
                    </label>
                    <select
                      value={specialHour.openTime}
                      onChange={(e) => handleSpecialHourChange(specialHour.id, 'openTime', e.target.value)}
                      disabled={specialHour.isClosed}
                      className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent disabled:bg-gray-100 disabled:text-gray-400"
                      style={{ maxHeight: '200px', overflowY: 'auto' }}
                    >
                      {timeOptions.map(time => (
                        <option key={time.value} value={time.value}>{time.display}</option>
                      ))}
                    </select>
                  </div>

                  {/* Closing Time */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Closes
                    </label>
                    <select
                      value={specialHour.closeTime}
                      onChange={(e) => handleSpecialHourChange(specialHour.id, 'closeTime', e.target.value)}
                      disabled={specialHour.isClosed}
                      className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent disabled:bg-gray-100 disabled:text-gray-400"
                      style={{ maxHeight: '200px', overflowY: 'auto' }}
                    >
                      {timeOptions.map(time => (
                        <option key={time.value} value={time.value}>{time.display}</option>
                      ))}
                    </select>
                  </div>

                  {/* Closed Checkbox */}
                  <div className="flex items-center">
                    <input
                      type="checkbox"
                      id={`special-closed-${specialHour.id}`}
                      checked={specialHour.isClosed}
                      onChange={(e) => handleSpecialHourChange(specialHour.id, 'isClosed', e.target.checked)}
                      className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                    />
                    <label htmlFor={`special-closed-${specialHour.id}`} className="ml-2 text-sm text-gray-700">
                      Closed
                    </label>
                  </div>

                  {/* Delete Button */}
                  <div className="flex justify-end">
                    <button
                      onClick={() => removeSpecialHourRow(specialHour.id)}
                      className="p-2 text-red-600 hover:text-red-800 hover:bg-red-50 rounded-lg transition-colors duration-200"
                      title="Delete exception"
                    >
                      <XMarkIcon className="h-5 w-5" />
                    </button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default OpeningHours;